import { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { format } from "date-fns/format";

import { Link as LinkView } from "@mui/material";
import Badge from "@mui/material/Badge";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import NotificationsIcon from "@mui/icons-material/Notifications";

import useFetch from "utils/fetch/useFetch";

import NotificationClient from "notification/NotificationClient";
import {
	ActionTarget,
	ActionType,
	Notification,
	NotificationOverview,
	NotificationType,
	TrajectoryNotification
} from "notification/NotificationTypes";

const PAGE_SIZE = 16;
const NON_CLICKABLE_ITEM_STYLE = {
	":hover": { backgroundColor: "transparent" },
	width: 1
};

const NotificationsMenu: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();

	const { call } = useFetch();

	const [anchorEl, setAnchor] = useState<HTMLElement>();
	const [notificationOverview, setNotificationOverview] =
		useState<NotificationOverview>();
	const [showing, setShowing] = useState<"unread" | "all">("unread");
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(false);

	// TODO: should be some kind of push.
	useEffect(() => {
		call(NotificationClient.getOverview()).then(setNotificationOverview);
	}, [location]);

	useEffect(() => {
		if (menuIsOpen()) {
			loadNotifications();
		}
	}, [page]);

	useEffect(() => {
		if (menuIsOpen()) {
			loadNotifications();
		}
	}, [showing]);

	function menuIsOpen(): boolean {
		return anchorEl !== undefined;
	}

	function loadNotifications(): void {
		setLoading(true);
		call(NotificationClient.getAll(page, PAGE_SIZE, showing === "unread"))
			.then(setNotificationOverview)
			.finally(() => setLoading(false));
	}

	function openUserMenu(event: React.MouseEvent<HTMLElement>): void {
		event.preventDefault();
		loadNotifications();
		setAnchor(event.currentTarget);
	}

	function closeUserMenu(): void {
		setAnchor(undefined);
	}

	function createNotificationItem(notification: Notification): JSX.Element {
		if (notification.type === NotificationType.TRAJECTORY) {
			return createTrajectoryNotificationItem(
				notification as TrajectoryNotification
			);
		}

		return <MenuItem key={notification.id} />;
	}

	function getTrajectoryNotificationTargetString(
		target: ActionTarget
	): string {
		switch (target) {
			case ActionTarget.FORM:
				return t("form");
			case ActionTarget.EVALUATION:
				return t("evaluation");
			case ActionTarget.REPORT:
				return t("report");
			default:
				return t("unknownTarget");
		}
	}

	function getTrajectoryNotificationActionString(type: ActionType): string {
		switch (type) {
			case ActionType.CREATE:
				return t("created");
			case ActionType.UPDATE:
				return t("updated");
			case ActionType.DELETE:
				return t("deleted");
			default:
				return t("unknowAction");
		}
	}

	function getTargetLinkSection(target: ActionTarget): string {
		switch (target) {
			case ActionTarget.FORM:
				return "form";
			case ActionTarget.EVALUATION:
				return "evaluation";
			case ActionTarget.REPORT:
				return "reports";
			default:
				return "form";
		}
	}

	function getTrajectoryLink(notification: TrajectoryNotification): string {
		let path =
			"/trajectory/" +
			notification.trajectory +
			"/" +
			getTargetLinkSection(notification.target);
		if (notification.target === ActionTarget.REPORT) {
			path += "/" + notification.targetId;
		}
		return path;
	}

	function createTrajectoryNotificationItem(
		notification: TrajectoryNotification
	): JSX.Element {
		return (
			<MenuItem
				key={notification.id}
				sx={{
					backgroundColor: notification.read ? "white" : "#F0F8FF"
				}}
				component={Link}
				to={getTrajectoryLink(notification)}
				onClick={() =>
					!notification.read &&
					call(NotificationClient.update(notification.id, true))
				}>
				<Grid container direction="column">
					<Grid item>
						<Typography variant="caption">
							{format(
								new Date(notification.created),
								"dd/MM/yyyy HH:mm:ss"
							)}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="body1">
							{t("trajectoryNotification", {
								target: getTrajectoryNotificationTargetString(
									notification.target
								),
								action: getTrajectoryNotificationActionString(
									notification.action
								)
							})}
						</Typography>
					</Grid>
				</Grid>
			</MenuItem>
		);
	}

	function markAllAsRead(): void {
		call(NotificationClient.updateAll()).then(() => {
			if (!notificationOverview) {
				return;
			}

			setNotificationOverview({
				...notificationOverview,
				unread: 0,
				notifications: notificationOverview.notifications.map(n => ({
					...n,
					read: true
				}))
			});
		});
	}

	function createMarkAllLink(): JSX.Element {
		if (
			notificationOverview === undefined ||
			notificationOverview.unread === 0
		) {
			return <></>;
		}

		return (
			<Grid item>
				<LinkView onClick={markAllAsRead}>{t("markAllRead")}</LinkView>
			</Grid>
		);
	}

	function createPagination(): JSX.Element {
		const notifications =
			showing === "unread"
				? notificationOverview?.unread
				: notificationOverview?.total;
		const pages = Math.ceil((notifications ?? 0) / PAGE_SIZE);
		return (
			<MenuItem
				key="pagination"
				sx={NON_CLICKABLE_ITEM_STYLE}
				disableRipple>
				<Pagination
					sx={{ margin: "auto" }}
					page={page + 1}
					count={pages}
					onChange={(event, page) => setPage(page - 1)}
				/>
			</MenuItem>
		);
	}

	function createMenuContent(): JSX.Element | JSX.Element[] {
		if (loading) {
			return (
				<MenuItem
					key="loading"
					sx={NON_CLICKABLE_ITEM_STYLE}
					disableRipple>
					<CircularProgress sx={{ margin: "auto" }} />
				</MenuItem>
			);
		}

		if (
			notificationOverview === undefined ||
			notificationOverview.notifications.length === 0
		) {
			return (
				<MenuItem
					key="noElements"
					sx={NON_CLICKABLE_ITEM_STYLE}
					disableRipple>
					<Typography>
						{t("noElementsFound", { type: "notification_other" })}
					</Typography>
				</MenuItem>
			);
		}

		const elements = notificationOverview.notifications.map(
			createNotificationItem
		);
		elements.push(createPagination());
		return elements;
	}

	return (
		<>
			<IconButton
				id="notification-icon"
				aria-label="notification-icon"
				aria-controls="menu-appbar"
				aria-haspopup="true"
				onClick={openUserMenu}
				color="inherit"
				size="large">
				<Badge
					badgeContent={notificationOverview?.unread ?? 0}
					color="secondary"
					variant="dot">
					<NotificationsIcon />
				</Badge>
			</IconButton>
			<Menu
				id="notification-menu"
				aria-label="notification-menu"
				sx={{ maxHeight: 0.7 }}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right"
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
				open={anchorEl !== undefined}
				onClose={closeUserMenu}>
				<MenuItem sx={NON_CLICKABLE_ITEM_STYLE} disableRipple={true}>
					<Grid container direction="column" sx={{ minWidth: 400 }}>
						<Grid item>
							<ToggleButtonGroup
								color="primary"
								value={showing}
								exclusive
								size="small"
								onChange={(event, value) => setShowing(value)}>
								<ToggleButton value="unread">
									{t("unread")}
								</ToggleButton>
								<ToggleButton value="all">
									{t("all")}
								</ToggleButton>
							</ToggleButtonGroup>
						</Grid>
						{createMarkAllLink()}
					</Grid>
				</MenuItem>
				{createMenuContent()}
			</Menu>
		</>
	);
};

export default NotificationsMenu;
